import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Gift, Check, Lock } from "lucide-react";
import T from "@/components/T";

export default function SeasonView({ ch, setCh }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => setData(await api.season());
  useEffect(() => { refresh(); }, []);

  const claim = async (tier) => {
    setBusy(true);
    try {
      const res = await api.seasonClaim(tier);
      setCh(res.character);
      await refresh();
      toast.success(`Season reward tier ${tier} claimed! +${res.reward.gold}g${res.reward.cosmetic ? ` + ${res.reward.cosmetic}` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="panel p-4 text-center text-[#666] italic text-sm">Consulting the almanac...</div>;

  const maxReq = data.rewards[data.rewards.length - 1].req;
  const pct = Math.min(100, (data.points / maxReq) * 100);

  return (
    <div className="space-y-2" data-testid="season-view">
      <div className="panel p-3">
        <div className="flex items-center justify-between mb-1">
          <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] flex items-center gap-2">
            <Trophy className="w-4 h-4 text-[#B89C3A]" strokeWidth={1.5} /> <T>Season Pass</T>
          </div>
          <span className="font-mono text-[10px] text-[#8c8c8c]">{data.month}</span>
        </div>
        <div className="vbar" style={{ height: 8 }}>
          <span style={{ width: `${pct}%`, background: "#B89C3A" }} />
        </div>
        <div className="font-mono text-[10px] text-[#E5C34A] mt-1" data-testid="season-points">
          {data.points} / {maxReq} points
        </div>
        <p className="italic text-[10px] text-[#666] mt-1">
          Earn 1 point per daily task claimed, +5 for completing the day.
        </p>
      </div>

      <div className="space-y-1">
        {data.rewards.map(r => {
          const claimed = data.claimed_tiers.includes(r.tier);
          const ready = data.points >= r.req;
          return (
            <div
              key={r.tier}
              data-testid={`season-tier-${r.tier}`}
              className={`panel-inner p-2 flex items-center gap-2 border-l-4 ${
                claimed ? "border-l-[#4A5D23]" : ready ? "border-l-[#E5C34A]" : "border-l-[#333]"
              }`}
            >
              <div className="w-8 h-8 flex items-center justify-center border border-[#333] font-heading text-sm text-[#E5C34A]">
                {r.tier}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading text-xs text-[#E2DFD8]">{r.name}</div>
                <div className="font-mono text-[10px] text-[#8c8c8c]">
                  Req {r.req} pts · +{r.gold}g
                  {r.cosmetic && <span className="text-[#B89C3A]"> · {r.cosmetic}</span>}
                </div>
              </div>
              {claimed ? (
                <span className="px-2 py-1 text-[10px] font-heading tracking-widest uppercase text-[#6E8B3D] border border-[#4A5D23] flex items-center gap-1">
                  <Check className="w-3 h-3" strokeWidth={2} /> Claimed
                </span>
              ) : ready ? (
                <button
                  data-testid={`season-claim-${r.tier}`}
                  onClick={() => claim(r.tier)}
                  disabled={busy}
                  className="px-2 py-1 text-[10px] font-heading tracking-widest uppercase border border-[#B89C3A] text-[#E5C34A]"
                >
                  <Gift className="w-3 h-3 inline mr-1" strokeWidth={1.5} /> Claim
                </button>
              ) : (
                <span className="px-2 py-1 text-[10px] font-heading tracking-widest uppercase text-[#666] border border-[#262626] flex items-center gap-1">
                  <Lock className="w-3 h-3" strokeWidth={1.5} /> Locked
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
